import type { AxiosResponse } from 'axios';
import type { ApiClientError } from 'api/client';

/**
 * Readers for the HTTP response behind an `ApiClientError`.
 *
 * The client puts it in one of two places depending on which rejection path
 * fired, and callers must not care which:
 *
 * - `axiosError.response` — a transport-level HTTP error (4xx/5xx).
 * - `response` — a 2xx whose body failed the `{ status: false }` envelope
 *   check, which is a normal way for this API to report a failure. No
 *   AxiosError exists on that path, so `axiosError` is undefined.
 *
 * Reading only `axiosError.response` (the shape most of this module was
 * written against) silently misses the whole second class, and used to miss
 * the first as well.
 */
const isAxiosResponseLike = (value: unknown): value is AxiosResponse =>
  value !== null &&
  typeof value === 'object' &&
  'status' in value &&
  typeof (value as { status: unknown }).status === 'number';

export const getErrorResponse = (error: unknown): AxiosResponse | undefined => {
  const apiError = error as ApiClientError | undefined;
  const fromAxios = apiError?.axiosError?.response;
  if (fromAxios) return fromAxios;

  return isAxiosResponseLike(apiError?.response)
    ? apiError?.response
    : undefined;
};

export const getErrorStatus = (error: unknown): number | undefined =>
  getErrorResponse(error)?.status;

/**
 * Machine-readable code of a coded api error
 * (`{ status: false, error_msg, error_data: { code } }`).
 */
export const getErrorCode = (error: unknown): string | undefined => {
  const data = getErrorResponse(error)?.data as
    | { error_data?: { code?: unknown } }
    | undefined;

  return typeof data?.error_data?.code === 'string'
    ? data.error_data.code
    : undefined;
};

/**
 * First message for `field` out of a Laravel validation body
 * (`{ errors: { <field>: string[] | string } }`), or undefined when the
 * response carries no error for that field.
 */
export const getFieldError = (
  error: unknown,
  field: string,
): string | undefined => {
  const data = getErrorResponse(error)?.data as
    | { errors?: Record<string, string[] | string | undefined> }
    | undefined;
  const value = data?.errors?.[field];
  const message = Array.isArray(value) ? value[0] : value;

  return typeof message === 'string' && message ? message : undefined;
};
